import React, {useContext} from 'react'
import {useSHCore, AjaxParam} from '@hooks/useSHCore';
import {getInitialComponentData} from "@helpers/HelperInitialPageState";
import {foreach, hasProperty} from '@helpers/HelperFunc'
import PriceVO from '@valueObjects/PriceVO'

const ContextServicePrice = React.createContext({})

/**
 * @function ContextServicePriceProvider
 * @property {{prices:Object<PriceData>}} contextData
 * @property {Function} getForIds За дадени service ID-та връща съответните цени
 *
 */
const ContextServicePriceProvider = ({children, contextName}) => {

	const resolvedName = typeof contextName === 'string' ? contextName : 'contextServicePriceProvider'
	const providerData = getInitialComponentData(resolvedName)

	/**
	 * За дадени service ID-та връща съответните цени
	 * @param {Array<string>} serviceIds
	 * @returns {Object<PriceData>}
	 */
	const getForIds = (serviceIds) => {
		if (!Array.isArray(serviceIds) || serviceIds.length === 0) {
			return {};
		}

		const prices = core.state.contextData?.prices || {};
		const data = {};

		serviceIds.forEach((serviceId) => {
			if (prices[serviceId]) {
				data[serviceId] = prices[serviceId];
			}
		});

		return data;
	}

	/**
	 * За дадено service ID връща съответните цени
	 * @param {string} serviceId
	 * @returns {Object<PriceData>}
	 */
	const getForId = (serviceId) => {
		return {...(core.state.contextData?.prices?.[serviceId] || {})}
	}

	/**
	 * Връща всички цени
	 * @returns {Object<PriceData>}
	 */
	const getAll = () => {
		return {...(core.state.contextData?.prices || {})}
	}

	/**
	 * За даден service Type и период, връща най-големия процент отстъпка
	 * @param {string} serviceType
	 * @param {string} term
	 * @returns number
	 */
	const getBiggerDiscount = (serviceType, term) => {
		return core.state.contextData.biggerDiscount?.[serviceType]?.[term] ?? 0;
	}

	/**
	 * За даден service Type и период, връща най-ниската цена
	 * @param {string} serviceType
	 * @param {number} term
	 * @param {boolean} yearPrice
	 * @returns number
	 */
	const getLowestPrice = (serviceType, term, yearPrice = false) => {
		return core.state.contextData.lowestPrice?.[serviceType]?.[term]?.[yearPrice === true ? 'year' : 'month'] ?? 0;
	}

	/**
	 * За дадено service ID връща съответните цени
	 * @param {string} serviceId
	 * @returns {Array.<PriceVO>}
	 */
	const getVOForId = (serviceId) => {
		const arrPrices = []
		if (hasProperty(core.state.contextData.prices, serviceId)) {
			foreach(core.state.contextData.prices[serviceId], (_index, prices) => {
				arrPrices.push(new PriceVO(prices))
			})
		}
		return arrPrices
	}

	/**
	 * @param {string} serviceId
	 * @param {string} term
	 * @returns {PriceVO}
	 */
	const getVOForIdAndTerm = (serviceId, term) => {
		let priceData = {}
		if (hasProperty(core.state.contextData.prices, serviceId)) {
			if (hasProperty(core.state.contextData.prices[serviceId], term)) {
				priceData = core.state.contextData.prices[serviceId][term]
			}
		}
		return new PriceVO(priceData);
	}

	/**
	 * @param {object} partialPrices
	 * @param {func} callback
	 * @returns {Object<PriceData>}
	 */
	const mergePrices = (partialPrices, callback = null) => {
		if (!partialPrices || typeof partialPrices !== 'object') {
			return;
		}

		core.setState((prev) => {
			const prevPrices = prev?.contextData?.prices || {};
			const nextPrices = {...prevPrices};

			// partialPrices: { [serviceId]: { [term]: priceData } }
			Object.keys(partialPrices).forEach((serviceId) => {
				const incomingPrice = partialPrices[serviceId];
				if (!incomingPrice || typeof incomingPrice !== 'object') return;

				nextPrices[serviceId] = {
					...(nextPrices[serviceId] || {}),
					...incomingPrice
				};
			});

			return {
				...prev,
				contextData: {
					...(prev.contextData || {}),
					prices: nextPrices
				}
			};
		}, () => {
			if (typeof callback === 'function') {
				callback(core.state.contextData.prices);
			}
		});
	};

	/**
	 * @param {Array} serviceIds
	 * @param {Array} terms
	 * @param {Function} callBackSuccess
	 * @param {Function} callBackError
	 * @param {string} promoCode
	 * @returns void
	 */
	const loadPricesForServiceIds = (serviceIds, terms = [], callBackSuccess = null, callBackError = null, promoCode = '') => {
		if (!Array.isArray(serviceIds) || serviceIds.length === 0) {
			if (typeof callBackSuccess === 'function') {
				callBackSuccess(null);
			}
			return;
		}

		const uniqueIds = Array.from(new Set(serviceIds.filter(Boolean)));
		const ajaxParam = new AjaxParam();

		ajaxParam.callbackSuccess = (data) => {
			const prices = data?.componentData?.prices || {};
			mergePrices(prices, (mergedPrices) => {
				if (typeof callBackSuccess === 'function') {
					callBackSuccess(prices, mergedPrices);
				}
			});

			// if (typeof callBackSuccess === 'function') {
			// 	callBackSuccess(prices, data);
			// }
		};

		ajaxParam.callbackError = (err) => {
			if (typeof callBackError === 'function') {
				callBackError(err);
			}
		};

		const actionData = {
			serviceIds: uniqueIds,
			terms,
			promoCode
		};

		// ajaxParam.cypressParamString = '?ContextServicePriceProvider-ActionGetDefaultState';

		core.ajaxCall('ActionGetDefaultState', actionData, ajaxParam);
	};

	/**
	 * Returns the first available term for each serviceId
	 * Example:
	 * {
	 *   bg: 12,
	 *   eu: 24
	 *   org: 12
	 * }
	 *
	 * @param {Object<string, Object<string, PriceData>>} prices
	 * @returns {Object<string, number>}
	 */
	const getFirstAvailableTerms = (prices = null) => {
		const sourcePrices = prices ?? core.state.contextData?.prices;

		if (!sourcePrices || typeof sourcePrices !== 'object') {
			return {};
		}

		const result = {};

		Object.entries(sourcePrices).forEach(([serviceId, terms]) => {
			if (!terms || typeof terms !== 'object') {
				return;
			}

			const termNumbers = Object.keys(terms)
			  .map(Number)
			  .filter((term) => !Number.isNaN(term));

			if (termNumbers.length) {
				result[serviceId] = Math.min(...termNumbers);
			}
		});

		return result;
	};

	const core = useSHCore({
		name: providerData.componentName,
		token: providerData.componentToken,
		initialState: {
			contextData: providerData.componentData,
			getForIds,
			getForId,
			getAll,
			getBiggerDiscount,
			getLowestPrice,
			getVOForId,
			getVOForIdAndTerm,
			mergePrices,
			loadPricesForServiceIds,
			getFirstAvailableTerms
		},
	});

	return (
	  <ContextServicePrice.Provider value={core.state}>
		  {children}
	  </ContextServicePrice.Provider>
	);
}

export {ContextServicePrice, ContextServicePriceProvider}

/**
 *
 * @returns {ContextServicePriceProvider}
 */
export const useServicePriceContext = () => useContext(ContextServicePrice);

/**
 * @typedef {Object} PriceData
 * @property {string} id - идентификатор на цената.
 * @property {string} priceNew - Цена за нова услуга.
 * @property {string} priceNewFormatted - Цена за нова услуга. Форматиран с валутата.
 * @property {number} basePriceNew - Базова цена за нова услуга
 * @property {string} priceRenew - priceRew за подновяване на услуга.
 * @property {string} priceRenewFormatted - priceRew за подновяване на услуга. Форматиран с валутата.
 * @property {number} basePriceRenew - Базова цена за подновяване на услуга
 * @property {string} term - Форматирано описание на период
 * @property {string} termEn - Форматирано описание на период АЕ
 * @property {string} currency - Валута
 * @property {number} discount - Закгръглена стойност на отстъпката
 * @property {number} discount_sum - Закгръглена стойност на отстъпката изчислена. Пример 50 лв.
 * @property {number} isPremium - Дали е премиум цена
 */